import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Undo2, CheckCircle, XCircle, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const ReturnsTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/returns`);
      setList(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  const handle = async (id, action) => {
    try {
      const { data } = await axios.patch(`${API}/admin/returns/${id}`, { action });
      if (action === "approve") {
        toast.success(`تمت الموافقة. خصم ${fmt(data.customer_debt_reduced_by)} د.ع من دين الزبون و ${fmt(data.driver_bonus_reduced_by)} من حوافز السائق`);
      } else {
        toast.success("تم الرفض");
      }
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  if (loading) return <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>;

  const pending = list.filter((r) => r.status === "pending");
  const done = list.filter((r) => r.status !== "pending");

  return (
    <div className="space-y-4" data-testid="returns-tab">
      <h2 className="font-heading font-bold text-lg text-[#111814] flex items-center gap-2">
        <Undo2 className="w-5 h-5 text-[#E85D45]" />
        طلبات الإرجاع المعلَّقة ({pending.length})
      </h2>

      {pending.length === 0 ? (
        <p className="text-center text-[#5C6E64] py-6">لا توجد طلبات إرجاع معلَّقة</p>
      ) : (
        pending.map((r) => (
          <div key={r.id} data-testid={`return-${r.id}`} className="bg-white border border-[#E85D45]/30 rounded-2xl p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-[#5C6E64]">فاتورة رقم</p>
                <p className="font-bold text-[#111814]">#{r.invoice_number || r.order_id.slice(0,6)}</p>
              </div>
              <span className="text-xs bg-[#F5A623]/20 text-[#F5A623] font-bold px-3 py-1 rounded-full">معلَّق</span>
            </div>
            <div className="bg-[#FFF8E1] rounded-xl p-3 text-sm space-y-1">
              <p><b>الزبون:</b> {r.user_name}{r.market_name && ` — ${r.market_name}`}</p>
              <p><b>السائق:</b> {r.driver_name}</p>
              {r.reason && <p className="text-xs text-[#5C6E64]"><b>السبب:</b> {r.reason}</p>}
            </div>
            <div className="space-y-1 text-sm py-2 border-t border-b border-[#E4EBE6]">
              {r.items.map((it, i) => (
                <div key={i} className="flex justify-between">
                  <span>{it.name_ar} × <span dir="ltr">{it.quantity}</span></span>
                  <span className="font-bold text-[#E85D45]" dir="ltr">{fmt(it.price * it.quantity)} د.ع</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs text-center">
              <div className="bg-[#F0F9FF] rounded-xl p-2"><p className="text-[#5C6E64]">عدد الكراتين</p><p className="font-bold text-base">{r.total_qty}</p></div>
              <div className="bg-[#FEF2F2] rounded-xl p-2"><p className="text-[#5C6E64]">المبلغ</p><p className="font-bold text-base" dir="ltr">{fmt(r.total_amount)} د.ع</p></div>
              <div className="bg-[#FFFBEB] rounded-xl p-2"><p className="text-[#5C6E64]">خصم حوافز السائق</p><p className="font-bold text-base" dir="ltr">{fmt(r.total_qty * 30)} د.ع</p></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handle(r.id, "approve")} data-testid={`approve-return-${r.id}`}
                className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1">
                <CheckCircle className="w-4 h-4" />موافقة
              </button>
              <button onClick={() => handle(r.id, "reject")} data-testid={`reject-return-${r.id}`}
                className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 rounded-full text-sm flex items-center justify-center gap-1">
                <XCircle className="w-4 h-4" />رفض
              </button>
            </div>
          </div>
        ))
      )}

      {done.length > 0 && (
        <>
          <h3 className="font-bold text-[#5C6E64] mt-6">السجلّ ({done.length})</h3>
          {done.map((r) => (
            <div key={r.id} className="bg-[#F3F6F4] border border-[#E4EBE6] rounded-xl p-3 text-sm flex justify-between">
              <span>#{r.invoice_number || r.order_id.slice(0,6)} — {r.user_name}</span>
              <span className={`font-bold ${r.status === "approved" ? "text-[#7ED321]" : "text-[#999]"}`}>
                {r.status === "approved" ? "تمت الموافقة" : "مرفوض"}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ReturnsTab;
